import { Observable, of, Subject, fromEvent, merge } from 'rxjs';
import Swal from "sweetalert2/dist/sweetalert2.js";
import { Injectable, OnDestroy } from "@angular/core";
import { ApiService } from "@core/services/api/api.service";
import { catchError, filter, takeUntil, debounceTime } from 'rxjs/operators';
import { SharedService } from "@core/services/shared/shared.service";
import { manageStorageService } from "@core/services/manage-storage/manage-storage.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { environment } from 'environments/environment';

const optionsGeo = {
  enableHighAccuracy: false, // enableHighAccuracy: true obliga al navegador a buscar mejor señal (GPS/Wi‑Fi scanning), aumenta la latencia y eleva la probabilidad de TIMEOUT o “no callback”
  maximumAge: 60000,  // Permitir caché corto (60s) reduce latencia y fallos.
  timeout: 7000 // timeout: 1000ms es demasiado agresivo para escenarios reales (laptops sin GPS, redes corporativas, permisos en prompt, etc.)
}, nameGeoStorage: string = 'geoLocationInfo';

export function obGeolocation() {
  return new Observable((observer) => {
    let watchId: any;
    const success = (position: any) => {
      observer.next(position);
      observer.complete();
    }, error = (err: any) => { observer.error(err); }, geolocation = navigator.geolocation;

    if (!geolocation) { observer.error('Geolocation is not supported by your browser'); }
    else { watchId = geolocation.getCurrentPosition(success, error, optionsGeo); }

    return () => navigator.geolocation.clearWatch(watchId);

  });
}

@Injectable()
export class geolocalizationService implements OnDestroy {
  // -----------------------------------------------------------------------------------------------------
  /** constructor  */
  // -----------------------------------------------------------------------------------------------------
  constructor(
    private _sharedInfo: SharedService,
    public _mStorage: manageStorageService,
    public _api: ApiService,
    private _utilS: UtilsService
  ) { }

  // -----------------------------------------------------------------------------------------------------
  // @ life cicle
  // -------------------------------------------------------------
  ngOnDestroy(): void {
    this.stopSafariPolling();
    this._unsubscribeGeo.complete()
    this._unsubscribeGeo.next()
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public declaration vars
  // -----------------------------------------------------------------------------------------------------
  titOperation: string = "";
  location$: Observable<any> = obGeolocation();
  flagGeo: boolean = false;
  geoInfo: any;
  // -----------------------------------------------------------------------------------------------------
  // @ Private declaration vars
  // -----------------------------------------------------------------------------------------------------
  private _unsubscribeGeo: Subject<void> = new Subject();
  private initialized = false;
  /** Intervalo de polling para Safari (reintento de permisos) */
  private _safariPollingInterval: any = null;
  /** Indica si el polling de Safari está activo */
  private _isSafariPolling: boolean = false;
  /** Flag para evitar que el mensaje de geolocalización se muestre más de una vez */
  private _geoMessageShown: boolean = false;
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  /**
   * ![reviewPermissionGeo]
   * @returns
   */
  reviewPermissionGeo() {
    let checkGeo = sessionStorage.getItem(nameGeoStorage) ? true : false;
    if (!checkGeo) { this.messageGeolocation(() => { this.handlePermission() }) }
    return checkGeo;
  }

  /* @doc
   putGeoInWS  ..> codeScreen
    codeOper:101000  // ..>  "Mercado de capitales",
    codeOper:102000 // ..>  "Fondos de inversión",
    codeOper:103010 // ..>  "Retiro con venta de fondos de inversión"
    codeOper:103020 // ..>  "Retiro de cuenta de inversión"
    codeOper:105010 // ..>  "Consulta saldos"
    codeOper:105110 // ..>  "Estado de Cuenta"
    codeOper:105120 // ..>  "Consulta Movimientos"
    codeOper:105210 // ..>  "Constancia de retención"
    codeOper:110010 // ..> "Información económica financiera",
    codeOper:110110  // ..> 'Cambio de contraseña',
    codeOper:110140  // ..> 'Cambio de clave de operación',
    codeOper:110150  // ..> 'Envío electrónico del estado de cuenta',
  */
  /**
   * ![putGeoInWS]
   * @param codeScreen
   */
  putGeoInWS(codeScreen: string): Promise<boolean> {
    let geoInfo = this._mStorage.getPropertie('geoLocationInfo') ?? {},
      dataUsr = this._mStorage.getPropertie('s_userSession') ?? {}

    let geoRqst = {
      "codOper": codeScreen,
      "lng": geoInfo?.Longitude,
      "lat": geoInfo?.Latitude,
      "claveUsr": dataUsr?.principal,
      "sid": "",
      "ctaAct": dataUsr?.contrato
    }

    if (typeof geoInfo !== 'string' && environment.activeGeo) {
      return new Promise<boolean>((rslvGeo, rjctGeo) => {
        this._api.geoRqst(geoRqst).subscribe
          ({
            next: (respnseGeoLoc: any) => { rslvGeo(true) },
            error: (eRR) => { rjctGeo(new Error(eRR?.message || 'Geolocation request failed')); }
          })
      })
    } else {
      return Promise.resolve(false);
    }
  }
  // -----------------------------------------------------------------------------------------------------
  // @ private methods
  // ---------------------------------------------------------------------------------------------
  /**
   * ![startSafariPermissionWatcher]
   * Safari no dispara `PermissionStatus.onchange`.
   *
   * Este watcher compara el estado actual del permiso (vía `permissions.query`)
   * contra el estado que fue procesado por `manageStatus` (guardado en sessionStorage).
   * Solo recarga si el estado **realmente cambió** desde que la página lo procesó.
   */
  private startSafariPermissionWatcher(): void {
    if (this._isSafariPolling) return;
    this._isSafariPolling = true;

    const GEO_PERM_KEY = '_geo_perm_state';

    // `focus` → regresar de cualquier ventana (Safari Settings, otra app)
    // `visibilitychange` → cambiar de pestaña
    merge(
      fromEvent(window, 'focus'),
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      )
    ).pipe(
      debounceTime(200),
      takeUntil(this._unsubscribeGeo)
    ).subscribe(() => {
      if (!navigator.permissions || !navigator.permissions.query) return;

      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        const storedState = sessionStorage.getItem(GEO_PERM_KEY);
        if (storedState && result.state !== storedState) {
          console.info(`🔄 Permiso de geolocalización cambió: ${storedState} → ${result.state}. Recargando...`);
          sessionStorage.setItem(GEO_PERM_KEY, result.state);
          location.reload();
        }
      }).catch(() => { });
    });
  }

  /**
   * ![stopSafariPolling]
   * Detiene el listener de visibilitychange de Safari y limpia el flag de recarga.
   */
  private stopSafariPolling(): void {
    if (this._safariPollingInterval) {
      clearInterval(this._safariPollingInterval);
      this._safariPollingInterval = null;
    }
    this._isSafariPolling = false;
    sessionStorage.removeItem('_geo_reload_ts');
  }

  /**
   * ![handleSafariFallback]
   * Maneja la geolocalización en Safari donde no existe `navigator.permissions.query`.
   * Intenta obtener la posición directamente; si falla con código 1 (denegado),
   * muestra el mensaje de alerta e inicia el polling periódico para detectar
   * cuando el usuario otorgue el permiso desde la configuración de Safari.
   * @param geolocationData - función para obtener datos de geolocalización vía Observable
   * @param removeStorage - función para limpiar el storage de geolocalización
   */
  private handleSafariFallback(geolocationData: () => void, removeStorage: () => void): void {
    navigator.geolocation.getCurrentPosition(
      (_position) => {
        // Permiso concedido → flujo normal
        this.flagGeo = true;
        geolocationData();
      },
      (error) => {
        switch (error.code) {
          case 1:
            // Permiso denegado → mostrar mensaje
            this.flagGeo = false;
            this.initialized = false;
            removeStorage();
            this.messageGeolocation(() => { this.handlePermission(); });
            break;
          case 2:
            // Dispositivo no puede obtener ubicación
            console.info(error.code, 'Safari: No puede obtener la ubicación');
            geolocationData();
            break;
          case 3:
            // Timeout → reintentar con flujo normal
            console.info(error.code, 'Safari: timeout geolocationInfo');
            geolocationData();
            break;
        }
      },
      optionsGeo
    );
  }

  private messageGeolocation(onGranted?: () => void) {
    // Evitar mostrar el mensaje más de una vez
    if (this._geoMessageShown) return;
    this._geoMessageShown = true;

    var message =
      "Por tu seguridad y por disposición oficial, Bx+ requiere conocer la geolocalización de tu dispositivo para utilizar nuestros servicios digitales. Es necesario contar con tu autorización para continuar. ";
    Swal.fire({
      title: this.titOperation, //"¡Aviso!",
      html: message,
      icon: "warning",
      confirmButtonText: "Aceptar",
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false
    }).then((result: any) => {
      if (result.isConfirmed) {
        // Resetear flag para permitir futuros intentos
        this._geoMessageShown = false;
        // Solicitar permisos de geolocalización al navegador
        navigator.geolocation.getCurrentPosition(
          () => {
            // Permiso concedido
            if (onGranted) {
              onGranted();
            } else {
              this.handlePermission();
            }
          },
          (error) => {
            // Permiso denegado o error — NO volver a mostrar el mensaje
            console.info('Geolocalización denegada o error:', error.code, error.message);
          },
          optionsGeo
        );
      }
    });
  }

  /**
   * ![handlePermission]
   * manage user permissions
   */
  handlePermission() {
    if (this.initialized) return;
    this.initialized = true;


    let removeStorage = () => { this._mStorage.clearStorage(nameGeoStorage); },
      /**
      * ![geolocationData]
      * Get Data from location
      */
      geolocationData = () => {
        this.location$.pipe(
          filter((value) => value !== null),
          takeUntil(this._unsubscribeGeo),
          catchError((error) => of({ error: { message: error.message, code: error.code } }))
        ).subscribe((position) => {

          if ('timestamp' in position &&
            !this._mStorage.getPropertie(nameGeoStorage)) {
            let objGeo = {
              Status: 0,
              Longitude: position["coords"]?.longitude,
              Latitude: position["coords"]?.latitude,
              Altitude: position["coords"].altitude || 0,
              HorizontalAccuracy: position["coords"].accuracy,
              AltitudeAccuracy: position["coords"].altitudeAccuracy || 0,
              Heading: position["coords"].heading || 0,
              Speed: position["coords"].speed || 0,
              Timestamp: position["timestamp"],
            }
            this.geoInfo = objGeo;
            this._mStorage.setItmStorage(nameGeoStorage, objGeo)

          }

          if (position?.error) {
            switch (position.error.code) {
              case 3:
                // ... error del timeout
                if (environment.showGeoConsole) report(JSON.stringify(position.error));
                this._sharedInfo.setData({ geo: false }, 'geolocationData');
                geolocationData();
                break;
              case 2:
                // ... dispositivo no puede obtener la ubicación
                if (environment.showGeoConsole) report(JSON.stringify(position.error));
                break;
              case 1:
                // ... no se tiene permisos :(
                if (environment.showGeoConsole) report(JSON.stringify(position.error));
                this.messageGeolocation(() => { geolocationData() });
                break;
            }
          }
        })
      },
      report = (state: any) => { console.info("🌎 Permission Geo " + state); },
      manageStatus = (result: any) => {
        // if (environment.showGeoConsole) report(result.state);

        // Guardar estado procesado para detección de cambios en Safari
        sessionStorage.setItem('_geo_perm_state', result.state);

        switch (result.state) {
          case "granted":
            this.flagGeo = true;
            geolocationData();
            break;
          // case "prompt":
          //   this.flagGeo = false
          //   removeStorage();
          //   geolocationData();
          //   break;

          case "prompt":
          case "denied":
            this.flagGeo = false;
            this.initialized = false;
            removeStorage();
            this.messageGeolocation(() => { geolocationData() });
            break;
        }


      };


    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        manageStatus(result);
        result.onchange = () => { manageStatus(result) };
      }).catch(() => {
        // Safari puede lanzar error en permissions.query
        this.handleSafariFallback(geolocationData, removeStorage);
      });
    } else {
      // Safari: no soporta navigator.permissions.query
      this.handleSafariFallback(geolocationData, removeStorage);
    }

    // Safari: activar watcher bidireccional de permisos
    // Solo para Safari (no soporta PermissionStatus.onchange)

    if (this._utilS.isSafari()) {
      this.startSafariPermissionWatcher();
    }
  } // end ![handlePermission]
}
